import { GridColDef } from "@mui/x-data-grid-pro";
import ImportBrokerModal from "Pages/CustomBrokerImport/ImportBrokerModal";
import {
  capitalize,
  GKButton,
  GKDataGrid,
  Grid2,
  InboundIcon,
  PlusIcon,
  PopConfirm,
  Show,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { PortfolioDetailsInterface } from "interfaces/Portfolio";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "store/store";
import {
  moneyCommaSeparator,
  moneyCommaSeparatorPoint,
} from "utils/MathFunction";
import axiosInstance from "utils/axiosInstance";
import { UserRoleTypes, handleCapitalize } from "utils/common";
import { extraColumnsField, matchDate, refetchQuery } from "utils/helpers";

interface Props {
  name: string;
  apiRoute: string;
  apiRouteKey: string;
  tableName: string;
  routeNavigate: {
    navigate: string;
    transactionType?: string;
    assetType?: string | string[];
    editNavigate?: string;
  };
  bulkDelete: string;
  exportRoute: string;
  portfolioId: string;
  brokerBtn?: boolean;
  portfolioDetailData?: PortfolioDetailsInterface;
  requestParams?: {
    key?: string;
    value?: string;
  };
}

const PortfolioSummaryTradeBookTab = (props: Props) => {
  const {
    name,
    apiRoute,
    apiRouteKey,
    tableName,
    routeNavigate,
    bulkDelete,
    exportRoute,
    portfolioId,
    brokerBtn = false,
    portfolioDetailData,
    requestParams,
  } = props;

  const { id } = useParams();

  const navigate = useNavigate();

  const userData = useAppSelector((state) => state?.user?.userData);

  const [selectedIds, setSelectedIds] = useState([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openImportBrokerModal, setOpenImportBrokerModal] = useState(false);

  const requestBody = useGenerateQueryParams(name, {
    [requestParams?.key]: requestParams?.value,
  });

  const { data, isFetching, refetch } = useFetchQuery({
    route: apiRoute,
    key: [apiRouteKey, requestBody],
    requestBody,
  });

  const { mutate: handleMultiDelete } = useMutation({
    mutationFn: ({ ids }: { ids: any }) =>
      axiosInstance?.delete(bulkDelete, {
        data: { ids: ids },
      }),
    onSuccess: () => {
      refetchQuery(apiRouteKey);
      setAnchorEl(null);
      setSelectedIds([]);
    },
  });

  const transactionColumns: (GridColDef & { visibleType?: string[] })[] = [
    {
      ...extraColumnsField("Date", "date", 120, 1),
      valueFormatter: (value) => matchDate(value.value),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioFDList",
        "portfolioFDPayoutList",
        "portfolioAIFList",
        "portfolioPositionList",
      ],
    },
    {
      ...extraColumnsField("Company Name", "companyName", 200, 1),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioFDList",
        "portfolioFDPayoutList",
        "portfolioAIFList",
        "portfolioPositionList",
      ],
    },
    {
      ...extraColumnsField("FD Account No.", "fdAccountNo", 150, 1),
      visibleType: ["portfolioFDList", "portfolioFDPayoutList"],
    },
    {
      ...extraColumnsField("Interest Type", "interestType", 150, 1),
      visibleType: ["portfolioFDList", "portfolioFDPayoutList"],
    },
    {
      ...extraColumnsField("NSE/BSE Code", "nseCode", 150, 1),
      valueGetter: (params) => params?.row?.nseCode || params?.row?.bseCode,
      visibleType: ["portfolioTradeBook"],
    },
    {
      ...extraColumnsField("Transaction Type", "transactionType", 150, 1),
      valueFormatter: (params) => handleCapitalize(params?.value),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioFDList",
        "portfolioFDPayoutList",
        "portfolioAIFList",
        "portfolioPositionList",
      ],
    },
    {
      ...extraColumnsField(
        "Sub Transaction Type",
        "subTransactionType",
        200,
        1,
      ),
      valueFormatter: (params) => capitalize(params?.value),
      visibleType: ["portfolioPositionList"],
    },
    {
      ...extraColumnsField("Option Type", "optionType", 120, 1),
      valueFormatter: (params) => capitalize(params?.value),
      visibleType: ["portfolioPositionList"],
    },
    {
      ...extraColumnsField("Quantity", "quantity", 120, 1),
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params?.row?.quantity || "0",
      valueFormatter: (params: any) => moneyCommaSeparatorPoint(params?.value),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioFDList",
        "portfolioFDPayoutList",
        "portfolioAIFList",
        "portfolioPositionList",
      ],
    },
    {
      ...extraColumnsField("Price", "price", 110, 1),
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params?.row?.price || "0",
      valueFormatter: (params) => moneyCommaSeparator(params.value),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioFDList",
        "portfolioFDPayoutList",
        "portfolioAIFList",
        "portfolioPositionList",
      ],
    },
    {
      ...extraColumnsField("Brokerage", "brokerage", 110, 1),
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.brokerage || "0",
      valueFormatter: (params) => moneyCommaSeparator(params.value),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioFDList",
        "portfolioAIFList",
        "portfolioPositionList",
      ],
    },
    {
      ...extraColumnsField(
        "Amount With Brokerage",
        "amountWithBrokerage",
        190,
        1,
      ),
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.amountWithBrokerage || "-",
      valueFormatter: (params) => moneyCommaSeparator(params.value),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioFDList",
        "portfolioAIFList",
        "portfolioPositionList",
      ],
    },
    {
      ...extraColumnsField("TDS", "tds", 100, 1),
      visibleType: ["portfolioFDList"],
      type: "number",
    },
    {
      ...extraColumnsField("STT", "stt", 100, 1),
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.stt || "0",
      valueFormatter: (params) => moneyCommaSeparator(params.value),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioAIFList",
      ],
    },
    {
      ...extraColumnsField("GST", "gst", 100, 1),
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.gst || "0",
      valueFormatter: (params) => moneyCommaSeparator(params.value),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioAIFList",
      ],
    },
    {
      ...extraColumnsField("Stamp Charges", "stampCharges", 150, 1),
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.stampCharges || "0",
      valueFormatter: (params) => moneyCommaSeparator(params.value),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioAIFList",
      ],
    },
    {
      ...extraColumnsField("Transaction Charges", "transactionCharges", 170, 1),
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.transactionCharges || "0",
      valueFormatter: (params) => moneyCommaSeparator(params.value),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioAIFList",
      ],
    },
    {
      ...extraColumnsField("SEBI Turnover Fees", "sebiTurnoverFees", 170, 1),
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.sebiTurnoverFees || "0",
      valueFormatter: (params) => moneyCommaSeparator(params.value),
      visibleType: ["portfolioTradeBook"],
    },
    {
      ...extraColumnsField("Other Charges", "otherCharges", 150, 1),
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.otherCharges || "0",
      valueFormatter: (params) => moneyCommaSeparator(params.value),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioAIFList",
      ],
    },
    {
      ...extraColumnsField("Bill Amount", "billAmount", 140, 1),
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.billAmount || "0",
      valueFormatter: (params) => moneyCommaSeparator(params.value),
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioFDList",
        "portfolioAIFList",
        "portfolioPositionList",
      ],
    },
    {
      ...extraColumnsField("ISIN Code", "isinCode", 150, 1),
      align: "right",
      headerAlign: "right",
      visibleType: [
        "portfolioTradeBook",
        "portfolioPrivateEquityList",
        "portfolioAIFList",
      ],
    },
    {
      ...extraColumnsField("Contract Note Number", "contractNoteNum", 190, 1),
      valueGetter: (params) => params.row.contractNoteNum || "-",
      valueFormatter: (params) => params.value,
      visibleType: ["portfolioTradeBook"],
    },
    {
      ...extraColumnsField("Broker Name", "brokerName", 150, 1),
      visibleType: ["portfolioTradeBook"],
    },
  ];

  return (
    <>
      <GKDataGrid
        density="compact"
        checkboxSelection
        onRowSelectionModelChange={(params) => {
          setSelectedIds(params);
        }}
        rowSelectionModel={selectedIds}
        loading={isFetching}
        columns={transactionColumns.filter((column) => {
          return column.visibleType?.includes(name);
        })}
        rows={data?.data?.data || []}
        onRowClick={(row) => {
          if (row?.row?.transactionType !== "BUY_BACK") {
            navigate(`${routeNavigate?.editNavigate}/${row?.id}`, {
              state: {
                transactionType: routeNavigate?.transactionType || "",
                assetType: routeNavigate?.assetType || "",
                id: row?.row?.portfolio,
              },
            });
          }
        }}
        initialState={{
          columns: {
            columnVisibilityModel: {
              stt: false,
              gst: false,
              stampCharges: false,
              otherCharges: false,
              transactionCharges: false,
              sebiTurnoverFees: false,
            },
          },
        }}
        totalCount={data?.data?.totalCount || 0}
        exportFile={{
          path: exportRoute,
          exportName: "portfolioTradeBook",
          paramsValue: {
            key: requestParams?.key,
            value: requestParams?.value,
          },
        }}
        name={name}
        headerComponent={
          userData?.userRole !== UserRoleTypes.client && (
            <Grid2 container gap={1} justifyContent={"end"}>
              {selectedIds?.length !== 0 && (
                <GKButton
                  variant="outlined"
                  size="medium"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                  }}
                >
                  Bulk Delete
                </GKButton>
              )}
              {selectedIds?.length !== 0 && (
                <GKButton
                  variant="outlined"
                  size="medium"
                  color="primary"
                  onClick={() => {
                    setSelectedIds([]);
                  }}
                >
                  Cancel
                </GKButton>
              )}
              {selectedIds?.length === 0 &&
                brokerBtn &&
                ["ADITYA_BIRLA_MONEY", "IIFL", "LATIN_MANHARLAL"]?.includes(
                  props?.portfolioDetailData?.broker,
                ) && (
                  <GKButton
                    variant="contained"
                    size="medium"
                    onClick={() => {
                      setOpenImportBrokerModal(true);
                    }}
                    startIcon={<InboundIcon width={16} height={16} />}
                  >
                    Import from broker
                  </GKButton>
                )}
              {name === "portfolioFDList" && selectedIds?.length === 0 && (
                <GKButton
                  variant="outlined"
                  size="medium"
                  onClick={() => {
                    navigate(
                      `/app/holdings/portfolio/${id}/fixed-deposit-interest`,
                    );
                  }}
                  startIcon={<PlusIcon width={14} />}
                >
                  Interest Payout
                </GKButton>
              )}
              {selectedIds?.length === 0 && (
                <GKButton
                  onClick={() => {
                    navigate(routeNavigate.navigate, {
                      state: {
                        transactionType: routeNavigate?.transactionType || "",
                        assetType: routeNavigate?.assetType || "",
                        id: portfolioId,
                      },
                    });
                  }}
                  variant="contained"
                  startIcon={<PlusIcon width={14} />}
                >
                  Add Trade
                </GKButton>
              )}
            </Grid2>
          )
        }
        tableName={tableName}
      />

      <Show.When isTrue={openImportBrokerModal}>
        <ImportBrokerModal
          open={openImportBrokerModal}
          setOpen={setOpenImportBrokerModal}
          refetch={refetch}
          type={portfolioDetailData?.broker}
          subType={"EQUITY"}
        />
      </Show.When>

      {Boolean(anchorEl) && (
        <PopConfirm
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          setOpen={setAnchorEl}
          onSuccess={() => handleMultiDelete({ ids: selectedIds })}
          message={`Are you sure that you want to delete the selected stocks & once deleted it cannot be reverted?`}
        />
      )}
    </>
  );
};

export default PortfolioSummaryTradeBookTab;
